import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { User, BadgeCheck, Video, Eye, Loader2, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Creators = () => {
  const navigate = useNavigate();
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCreators();
  }, []);

  const fetchCreators = async () => {
    try {
      const response = await axios.get(`${API}/creators`);
      setCreators(response.data.creators || []);
    } catch (error) {
      console.error('Error fetching creators:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 px-4 md:px-8 lg:px-16 py-8" data-testid="creators-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 
            className="text-4xl md:text-6xl font-normal tracking-wide uppercase mb-4"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            Featured Creators
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Discover amazing content creators on VelvetStream. Follow your favorites and never miss their latest uploads.
          </p>
        </div>

        {/* Creators Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : creators.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-6">No creators yet. Be the first!</p>
            <Button 
              onClick={() => navigate('/subscription')}
              className="bg-white text-black hover:bg-gray-200"
            >
              Become a Creator
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {creators.map(creator => (
              <div 
                key={creator.user_id}
                onClick={() => navigate(`/creator/${creator.user_id}`)}
                className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden cursor-pointer group hover:border-white/30 transition-all"
                data-testid={`creator-card-${creator.user_id}`}
              >
                {/* Cover */}
                <div 
                  className="h-24 bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A]"
                  style={creator.cover_image ? { backgroundImage: `url(${creator.cover_image})`, backgroundSize: 'cover' } : {}}
                />
                
                {/* Avatar */}
                <div className="px-4 -mt-10 relative z-10">
                  <div className="w-20 h-20 rounded-full border-4 border-[#121212] bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center overflow-hidden">
                    {creator.avatar ? (
                      <img src={creator.avatar} alt={creator.display_name} className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-8 h-8 text-white" />
                    )}
                  </div>
                </div>

                {/* Info */}
                <div className="p-4 pt-2">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-medium text-white group-hover:text-gray-300 transition-colors">
                      {creator.display_name}
                    </h3>
                    {creator.verified && (
                      <BadgeCheck className="w-4 h-4 text-amber-500" />
                    )}
                  </div>
                  <p className="text-gray-500 text-sm capitalize mb-3">
                    {creator.subscription_tier || 'Creator'}
                  </p>
                  
                  {creator.bio && (
                    <p className="text-gray-400 text-sm line-clamp-2 mb-3">{creator.bio}</p>
                  )}

                  <div className="flex gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Video className="w-3 h-3" />
                      {creator.video_count || 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {creator.total_views || 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {creator.follower_count || 0}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-purple-900/30 to-amber-900/30 border border-white/10 rounded-lg p-8 md:p-12">
            <h2 
              className="text-3xl font-normal tracking-wide uppercase mb-4"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            >
              Want to become a creator?
            </h2>
            <p className="text-gray-400 mb-6 max-w-xl mx-auto">
              Upload your content, build your audience, and start earning. Join our growing community of creators.
            </p>
            <div className="flex gap-4 justify-center">
              <Button 
                onClick={() => navigate('/subscription')}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                Subscribe as Creator
              </Button>
              <Button 
                onClick={() => navigate('/become-model')}
                variant="outline"
                className="border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
              >
                Apply as Model
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Creators;
